/**
 * #1630 — dark-mode brand-override decision logic.
 *
 * Codex+Gemini P1 (issue #1630): BrandingContext derives
 * --brand-primary-hover/-light/-accent-soft in JS from the org's LIGHT
 * colors regardless of theme, and writes them as inline styles on
 * <html> — which out-specify the [data-theme='dark'] stylesheet block
 * and clobber the hand-designed dark brand tokens.
 *
 * Fix (Gate-1 decision log, Gilbert 2026-07-02): an explicit precedence
 * contract —
 *   base tokens → theme-mode block → org override → derived
 * — with three cases in dark mode:
 *
 *  1. Tacit (no org branding override): BrandingContext must SKIP writing
 *     inline overrides so the [data-theme='dark'] CSS block (Tacit's
 *     hand-designed dark palette, theme-variables.css) wins.
 *  2. Praxis (Umuzi): curated dark values, SPLIT BY ROLE per the decision
 *     log — spec teal #4A7A95 is STRUCK:
 *       - --brand-accent-soft (gradient endpoint / large fading surfaces:
 *         avatar gradient, user message bubble) → ember #B35530
 *       - --brand-accent (direct accent: chips, links, outline buttons)
 *         → sand-gold #C9A15E (use lighter #D8B87E where accent renders
 *         as text on the darkest surfaces — component-level, not a token
 *         this layer emits)
 *       - primary stays the spec's #FF7A45 / hover #FF8E5F / light #FFB99A
 *     Maps 1:1 onto the existing two-token structure — no new tokens.
 *  3. Any other branded org: no hand-designed dark set exists yet, so
 *     auto-derive one by lightening the org's light-mode primary/accent to
 *     a WCAG contrast floor against the dark canvas ("derived" layer).
 *
 * Pure decision function — no DOM, no React. BrandingContext.jsx applies
 * the result via root.style.setProperty/removeProperty.
 */
import { hexToRgb, lightenHex, mixHex, luminance, ensureContrastFloor } from './colorMath';

// Must match theme-variables.css [data-theme='dark'] --color-bg-primary
// (Tacit's dark canvas — the single universal dark background regardless
// of org, mirroring how light mode already has one universal --color-bg-*
// set shared by every org).
export const DARK_CANVAS_BG = '#0A0E1A';

// Praxis (Umuzi) curated dark brand — Gate-1 decision log, issue #1630.
export const PRAXIS_DARK_BRAND = Object.freeze({
  primary: '#FF7A45',
  primaryHover: '#FF8E5F',
  primaryLight: '#FFB99A',
  accent: '#C9A15E',       // sand-gold — direct accent (chips/links/outline buttons)
  accentSoft: '#B35530',   // ember — gradient endpoint (avatar gradient, user bubble)
});

function brandTokens({ primary, primaryHover, primaryLight, accent, accentSoft }) {
  const [r, g, b] = hexToRgb(primary);
  return {
    '--brand-primary': primary,
    '--brand-primary-rgb': `${r}, ${g}, ${b}`,
    '--brand-primary-hover': primaryHover,
    '--brand-primary-light': primaryLight,
    '--brand-accent': accent,
    '--brand-accent-soft': accentSoft,
  };
}

/**
 * @param {object} params
 * @param {boolean} params.isTacitDefault - true when the org has no
 *   branding override (i.e. is running Tacit's own defaults)
 * @param {string} params.appName - resolved branding.appName
 * @param {string} params.primaryColor - org's light-mode primary_color
 * @param {string} params.accentColor - org's light-mode accent_color
 * @returns {{ mode: 'skip' } | { mode: 'set', tokens: Record<string,string> }}
 *   'skip' → remove any inline brand overrides so the CSS dark block wins.
 *   'set'  → explicit token values to write as inline overrides.
 */
export function computeDarkBrandOverride({ isTacitDefault, appName, primaryColor, accentColor }) {
  if (isTacitDefault) {
    return { mode: 'skip' };
  }

  if (appName === 'Praxis') {
    return {
      mode: 'set',
      tokens: brandTokens({
        primary: PRAXIS_DARK_BRAND.primary,
        primaryHover: PRAXIS_DARK_BRAND.primaryHover,
        primaryLight: PRAXIS_DARK_BRAND.primaryLight,
        accent: PRAXIS_DARK_BRAND.accent,
        accentSoft: PRAXIS_DARK_BRAND.accentSoft,
      }),
    };
  }

  // Other branded orgs: no hand-designed dark set — derive one.
  const darkPrimary = ensureContrastFloor(primaryColor, DARK_CANVAS_BG, 4.5);
  // Mirrors BrandingContext's light-mode soft-accent rule: very-dark accents
  // (e.g. #000000) mix toward primary first, rather than lightening black
  // directly (which would just produce shades of gray).
  const accentBase = luminance(accentColor) < 0.05
    ? mixHex(primaryColor, accentColor, 0.3)
    : accentColor;
  const darkAccent = ensureContrastFloor(accentBase, DARK_CANVAS_BG, 3);

  return {
    mode: 'set',
    tokens: brandTokens({
      primary: darkPrimary,
      primaryHover: lightenHex(darkPrimary, 0.12),
      primaryLight: lightenHex(darkPrimary, 0.28),
      accent: darkAccent,
      accentSoft: lightenHex(darkAccent, 0.10),
    }),
  };
}
